/**
 * New folder in a file share (Task 7.15): creates a REAL directory through `azure-storage:directory-create` (unlike a
 * blob virtual folder). The name is checked live with the share protocol's rules (`fileNameProblem`); main's refusal
 * (for example "already exists") shows inline and the dialog stays open.
 */
import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { fileNameProblem, joinSharePath } from '../../../../../core/engines/azureStorageFilePaths';
import type { DirectoryRef, FileShareProtocol, ShareRef } from '../../../../../types/azureStorage';
import { createFileDirectory } from '../fileTransfers';

type Props = {
  connectionId: string;
  tabId?: string;
  parent: ShareRef | DirectoryRef;
  protocol: FileShareProtocol;
  onClose: () => void;
  onCreated: () => void;
};

export function NewDirectoryDialog({ connectionId, tabId, parent, protocol, onClose, onCreated }: Props) {
  const [serverError, setServerError] = useState<string>();
  const parentPath = parent.kind === 'share' ? '' : parent.path;
  const form = useForm({
    defaultValues: { name: '' },
    onSubmit: async ({ value }) => {
      setServerError(undefined);
      const directory: DirectoryRef = { kind: 'directory', share: parent.share, path: joinSharePath(parentPath, value.name.trim()) };
      const result = await createFileDirectory({ connectionId, tabId, directory, source: 'toolbar', protocol });
      if (!result.ok) {
        setServerError(result.error.message);
        return;
      }
      onCreated();
      onClose();
    },
  });
  const validate = ({ value }: { value: string }) => fileNameProblem(value.trim(), protocol);
  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent data-testid="file-new-folder-dialog">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>Creates the folder in {parentPath ? `${parent.share}/${parentPath}` : parent.share}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }} className="space-y-4">
          <form.Field name="name" validators={{ onChange: validate, onSubmit: validate }}>
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="file-new-folder-name">Folder name</Label>
                <Input spellCheck={false} autoComplete="off" id="file-new-folder-name" value={field.state.value} onBlur={field.handleBlur} onChange={(event) => field.handleChange(event.target.value)} autoFocus data-testid="file-new-folder-name" />
                {field.state.meta.errors[0] && <p className="text-sm text-destructive" data-testid="file-new-folder-problem">{field.state.meta.errors[0]}</p>}
              </div>
            )}
          </form.Field>
          {serverError && <p className="text-sm text-destructive" role="alert" data-testid="file-new-folder-error">{serverError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => <Button type="submit" disabled={!canSubmit || isSubmitting} data-testid="file-new-folder-create">Create folder</Button>}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
